import apiClient from "@/lib/apiClient";
import type {
  ClientAnnouncementsListParams,
  ClientAnnouncementDetailItem,
  ClientAnnouncementListItem,
} from "../types/client-announcements.types";
import { ANNOUNCEMENT_DISPLAY_TYPE } from "../types/client-announcements.types";

const BASE = "/api/client/announcements";

/**
 * GET /api/client/announcements/categories
 * Get categories for filter (departments and educations with announcements)
 * Response: { status, data: { success, status, data: { items }, message } }
 */
export async function getCategories() {
  const { data } = await apiClient.get(`${BASE}/categories`);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  return raw?.data?.data?.items ?? raw?.data?.items ?? raw?.items ?? [];
}

function normalizeAnnouncementsList(data: unknown): {
  items: ClientAnnouncementListItem[];
  total: number;
} {
  const raw = data as {
    data?: unknown;
    count?: number;
    total?: number;
  };
  const inner = raw.data as
    | {
        data?: unknown;
        items?: unknown;
        count?: number;
        total?: number;
      }
    | undefined;
  const items = (
    Array.isArray(inner?.data)
      ? inner.data
      : Array.isArray(inner?.items)
        ? inner.items
        : Array.isArray(raw.data)
          ? raw.data
          : []
  ) as ClientAnnouncementListItem[];

  return {
    items,
    total: inner?.count ?? inner?.total ?? raw.count ?? raw.total ?? items.length,
  };
}

/**
 * GET /api/client/announcements
 * List announcements for current user (Genel, Özel or all).
 * Response: { status, data: { success, status, data: [], message, count } }
 */
export async function getAnnouncements(params?: ClientAnnouncementsListParams) {
  const { data } = await apiClient.get(BASE, {
    params: {
      type: params?.type ?? "all",
      displayType: params?.displayType,
      search: params?.search,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
      sortBy: params?.sortBy ?? "startDate",
      sortOrder: params?.sortOrder ?? "desc",
      page: params?.page ?? 1,
      limit: params?.limit ?? 12,
    },
  });
  return normalizeAnnouncementsList(data);
}

/**
 * GET /api/client/announcements?displayType=1&limit=100
 * Girişten hemen sonra modal olarak gösterilecek duyurular.
 *
 * Yalnızca POPUP olarak işaretlenenler çekilir; anasayfa duyuruları kullanıcının
 * önüne modal olarak çıkmaz. Backend ayrıca "bir daha gösterme" tercihi
 * verilenleri zaten listeden düşürüyor.
 */
export async function getLoginAnnouncements() {
  const { data } = await apiClient.get(BASE, {
    params: { displayType: ANNOUNCEMENT_DISPLAY_TYPE.POPUP, limit: 100 },
    skipErrorToast: true,
  });
  return normalizeAnnouncementsList(data);
}

/**
 * GET /api/client/announcements/{id}
 * Get announcement detail by ID
 */
/**
 * GET /api/client/announcements/{id}
 * Get announcement detail by ID.
 * Response shape may be { status, data: { success, status, data: detail, message } } or { data: detail }.
 */
export async function getAnnouncementById(id: string) {
  const { data } = await apiClient.get(`${BASE}/${id}`);
  const inner = (data as { data?: { data?: ClientAnnouncementDetailItem } })
    .data;
  const detail =
    inner && typeof inner === "object" && "data" in inner
      ? (inner as { data: ClientAnnouncementDetailItem }).data
      : (data as { data: ClientAnnouncementDetailItem }).data;
  return detail;
}

/**
 * PATCH /api/client/announcements/{id}/preference
 * Persist current user's "do not show again" preference for an announcement.
 */
export async function saveAnnouncementPreference(
  id: string,
  hideAgain: boolean,
) {
  const { data } = await apiClient.patch(
    `${BASE}/${id}/preference`,
    { hideAgain },
    { skipErrorToast: true },
  );
  return data;
}

export const clientAnnouncementsApi = {
  getCategories,
  getAnnouncements,
  getLoginAnnouncements,
  getAnnouncementById,
  saveAnnouncementPreference,
};
